import React from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { cn } from '@/utils';
import { Loader2 } from 'lucide-react';

export type SelectValueType = string | number;

export type SelectOption<T extends SelectValueType = string> = {
  label: string;
  value: T;
};

interface CustomSelectProps<T extends SelectValueType = string> {
  value?: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
}

const CustomSelect = <T extends SelectValueType>({
  value,
  options,
  onChange,
  placeholder = 'Select option',
  disabled = false,
  isLoading = false,
  triggerClassName,
  contentClassName,
}: CustomSelectProps<T>) => {
  return (
    <Select
      disabled={disabled}
      value={value !== undefined ? String(value) : ''}
      onValueChange={(v) => {
        const selected = options.find((o) => String(o.value) === v);
        if (selected) onChange(selected.value);
      }}
    >
      <SelectTrigger
        className={cn(
          'w-full font-medium text-white',
          disabled && 'opacity-50 cursor-not-allowed',
          triggerClassName
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>

      <SelectContent className={cn('z-[100]', contentClassName)}>
        {/* LOADING STATE */}
        {isLoading && (
          <div className="flex items-center justify-center py-3 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading...
          </div>
        )}

        {/* EMPTY STATE */}
        {!isLoading && options.length === 0 && (
          <div className="py-3 text-center text-sm text-muted-foreground">No options available</div>
        )}

        {/* OPTIONS */}
        {!isLoading &&
          options.map((option) => {
            const isSelected = String(option.value) === String(value);

            return (
              <SelectItem
                key={option.value}
                value={String(option.value)}
                className={cn(
                  isSelected &&
                    ' '
                )}
              >
                {option.label}
              </SelectItem>
            );
          })}
      </SelectContent>
    </Select>
  );
};

export default CustomSelect;
